import { useEffect, useMemo, useState } from 'react'

import { getApartments } from '@services/livability/apartments'
import type {
  ApartmentFilterOptions,
  ListingType
} from '@services/livability/types'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'

import type { ApartmentFilters } from '../../utils/apartmentFilterUrl'
import { filtersToApiParams } from '../../utils/apartmentFilterUrl'
import { MoreFiltersTrigger } from './FilterBar'
import { BOOLEAN_DEFS, applyFilterPatch } from './filterDefs'

const COUNT_DEBOUNCE_MS = 350

/** Value used for the "any" option — shadcn Select forbids empty item values. */
const ANY = '__any'

/** Filter keys managed by this dialog (everything outside the main bar). */
const MORE_KEYS = [
  'minArea',
  'maxArea',
  'minPricePerM2',
  'maxPricePerM2',
  'condition',
  'buildingType',
  'minIndex',
  ...BOOLEAN_DEFS.map((def) => def.key)
] as const

export function countActiveMoreFilters(filters: ApartmentFilters): number {
  let count = 0
  if (filters.minArea !== undefined || filters.maxArea !== undefined) count++
  if (
    filters.minPricePerM2 !== undefined ||
    filters.maxPricePerM2 !== undefined
  ) {
    count++
  }
  if (filters.condition) count++
  if (filters.buildingType) count++
  if (filters.minIndex !== undefined) count++
  for (const def of BOOLEAN_DEFS) {
    if (filters[def.key] === true) count++
  }
  return count
}

interface MoreFiltersDialogProps {
  filters: ApartmentFilters
  onChange: (next: ApartmentFilters) => void
  filterOptions?: ApartmentFilterOptions
  listingType: ListingType
}

export function MoreFiltersDialog({
  filters,
  onChange,
  filterOptions,
  listingType
}: MoreFiltersDialogProps) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<ApartmentFilters>(filters)
  const [total, setTotal] = useState<number | null>(null)

  const activeCount = useMemo(
    () => countActiveMoreFilters(filters),
    [filters]
  )

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) setDraft(filters)
    setOpen(nextOpen)
  }

  // Debounced result-count preview for the current draft.
  useEffect(() => {
    if (!open) return

    const controller = new AbortController()
    const timer = window.setTimeout(async () => {
      try {
        const response = await getApartments(
          filtersToApiParams(draft, { page: 1, limit: 1 }),
          listingType,
          controller.signal
        )
        setTotal(response.total)
      } catch {
        setTotal(null)
      }
    }, COUNT_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [draft, listingType, open])

  const patchDraft = (patch: Partial<ApartmentFilters>) => {
    setDraft((current) => applyFilterPatch(current, patch))
  }

  const clearMoreFilters = () => {
    setDraft((current) => {
      const next = { ...current }
      for (const key of MORE_KEYS) delete next[key]
      return next
    })
  }

  const apply = () => {
    onChange(draft)
    setOpen(false)
  }

  return (
    <>
      <MoreFiltersTrigger
        activeCount={activeCount}
        onClick={() => handleOpenChange(true)}
      />
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="app-chrome max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Viac filtrov</DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            <RangeField
              label="Plocha (m²)"
              min={draft.minArea}
              max={draft.maxArea}
              onCommit={(min, max) =>
                patchDraft({ minArea: min, maxArea: max })
              }
            />

            <RangeField
              label="Cena za m² (€)"
              min={draft.minPricePerM2}
              max={draft.maxPricePerM2}
              onCommit={(min, max) =>
                patchDraft({ minPricePerM2: min, maxPricePerM2: max })
              }
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <SelectField
                label="Stav bytu"
                value={draft.condition}
                options={filterOptions?.conditions ?? []}
                onChange={(value) => patchDraft({ condition: value })}
              />
              <SelectField
                label="Typ budovy"
                value={draft.buildingType}
                options={filterOptions?.building_types ?? []}
                onChange={(value) => patchDraft({ buildingType: value })}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium">
                Minimálne hodnotenie (0–100)
              </Label>
              <Input
                type="number"
                min={0}
                max={100}
                placeholder="napr. 60"
                value={draft.minIndex?.toString() ?? ''}
                onChange={(e) => {
                  const parsed = e.target.value.trim()
                    ? Math.min(100, Math.max(0, Number(e.target.value)))
                    : undefined
                  patchDraft({
                    minIndex: Number.isFinite(parsed) ? parsed : undefined
                  })
                }}
                className="w-32"
              />
            </div>

            <div className="space-y-2.5">
              <Label className="text-sm font-medium">Vybavenie a dostupnosť</Label>
              <div className="grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
                {BOOLEAN_DEFS.map((def) => (
                  <label
                    key={def.id}
                    className="flex cursor-pointer items-center gap-2.5 text-sm text-ink"
                  >
                    <Checkbox
                      checked={draft[def.key] === true}
                      onCheckedChange={(checked) =>
                        patchDraft({
                          [def.key]: checked === true ? true : undefined
                        })
                      }
                    />
                    {def.label}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="mt-2 gap-2 border-t border-ink/[0.06] pt-4 sm:justify-between">
            <Button type="button" variant="ghost" onClick={clearMoreFilters}>
              Vymazať
            </Button>
            <Button type="button" onClick={apply}>
              {total === null ? 'Zobraziť byty' : `Zobraziť ${total} bytov`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function RangeField({
  label,
  min,
  max,
  onCommit
}: {
  label: string
  min?: number
  max?: number
  onCommit: (min: number | undefined, max: number | undefined) => void
}) {
  const parse = (raw: string): number | undefined => {
    if (!raw.trim()) return undefined
    const value = Number(raw)
    return Number.isFinite(value) ? value : undefined
  }

  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          placeholder="Min"
          value={min?.toString() ?? ''}
          onChange={(e) => onCommit(parse(e.target.value), max)}
        />
        <span className="text-muted-foreground">–</span>
        <Input
          type="number"
          placeholder="Max"
          value={max?.toString() ?? ''}
          onChange={(e) => onCommit(min, parse(e.target.value))}
        />
      </div>
    </div>
  )
}

function SelectField({
  label,
  value,
  options,
  onChange
}: {
  label: string
  value?: string
  options: string[]
  onChange: (value: string | undefined) => void
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">{label}</Label>
      <Select
        value={value ?? ANY}
        onValueChange={(next) => onChange(next === ANY ? undefined : next)}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Akýkoľvek" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>Akýkoľvek</SelectItem>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
